import { forwardRef, memo, useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { Tabs, type TabItem } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from '../types';
import { useCommunicationStep } from './useCommunicationStep';
import {
  CandidateCommunicationPanel,
  InterviewerCommunicationPanel,
  SelfScheduleCommunicationPanel,
} from './panels';

export const InterviewActivityCommunication = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    ({ payloadRef, prefillVersion = 0 }, ref) => {
      const {
        state,
        dispatch,
        fieldErrors,
        submitAttempt,
        candidateCalendarTemplateOptions,
        candidateEmailTemplateOptions,
        interviewerCalendarTemplateOptions,
        interviewerEmailTemplateOptions,
        selfScheduleTemplateOptions,
        fromOptions,
        ccBccOptions,
        panelVariableProps,
        emailApiRefs,
      } = useCommunicationStep({ payloadRef, prefillVersion, ref });

      const tabItems: TabItem[] = useMemo(
        () => [
          {
            label: 'Candidate communication',
            value: 'candidate-communication',
            panel: (
              <CandidateCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                submitAttempt={submitAttempt}
                candidateCalendarTemplateOptions={
                  candidateCalendarTemplateOptions
                }
                candidateEmailTemplateOptions={candidateEmailTemplateOptions}
                fromOptions={fromOptions}
                ccBccOptions={ccBccOptions}
                emailApiRefs={emailApiRefs}
                {...panelVariableProps}
              />
            ),
          },
          {
            label: 'Interviewer communication',
            value: 'interviewer-communication',
            panel: (
              <InterviewerCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                submitAttempt={submitAttempt}
                interviewerCalendarTemplateOptions={
                  interviewerCalendarTemplateOptions
                }
                interviewerEmailTemplateOptions={
                  interviewerEmailTemplateOptions
                }
                fromOptions={fromOptions}
                ccBccOptions={ccBccOptions}
                emailApiRefs={emailApiRefs}
                {...panelVariableProps}
              />
            ),
          },
          {
            label: 'Self-schedule slot selection email',
            value: 'self-schedule-slot-selection-email',
            panel: (
              <SelfScheduleCommunicationPanel
                state={state}
                dispatch={dispatch}
                fieldErrors={fieldErrors}
                submitAttempt={submitAttempt}
                selfScheduleTemplateOptions={selfScheduleTemplateOptions}
                fromOptions={fromOptions}
                ccBccOptions={ccBccOptions}
                emailApiRefs={emailApiRefs}
                {...panelVariableProps}
              />
            ),
          },
        ],
        [
          state,
          dispatch,
          fieldErrors,
          submitAttempt,
          candidateCalendarTemplateOptions,
          candidateEmailTemplateOptions,
          interviewerCalendarTemplateOptions,
          interviewerEmailTemplateOptions,
          selfScheduleTemplateOptions,
          fromOptions,
          ccBccOptions,
          panelVariableProps,
          emailApiRefs,
        ]
      );

      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          minW={0}
          flex="1"
          flexDirection="column"
          overflow="hidden"
        >
          <Tabs.Root
            variant="side"
            items={tabItems}
            // Keep every sub-tab's editors mounted so they never remount on a
            // tab switch — `validate()`'s `focusFirstError()` then lands on the
            // bad variable on the FIRST click (no waiting for a fresh TipTap
            // instance to initialise after switching tabs).
            preservePanels
            value={state.activeSubTab}
            onValueChange={(next) => {
              dispatch({
                type: 'SET_ACTIVE_SUB_TAB',
                payload: next as typeof state.activeSubTab,
              });
            }}
            className="h-full min-h-0 w-full min-w-0 flex-1 grid-rows-[minmax(0,1fr)] gap-3"
            tabListWrapperClassName="self-start"
            panelClassName="min-h-0 h-full max-h-full min-w-0 overflow-y-auto overflow-x-hidden pb-1 pr-4"
          />
        </Flex>
      );
    }
  )
);

InterviewActivityCommunication.displayName = 'InterviewActivityCommunication';
